<script lang="ts">
import { defineComponent, h, unref, watch } from 'vue';

import { classNames, hMergeSlot, warn } from '@components/utils';

import {
  makeInvertColor,
  makeLayoutProps,
  makeThemeProps,
  provideTheme,
  useInvertColor,
  useDisplay,
  useLayout,
  useRender,
  makeTransparentProps,
  useTransparent
} from '../../composable';
import { LayoutResizeObserver } from '../CdsLayout/components';
import { useInjectLayout } from '../CdsLayout/context';

export const componentName = 'CdsFooter';
export default defineComponent({
  name: componentName,
  props: {
    ...makeLayoutProps(),
    ...makeInvertColor(),
    ...makeTransparentProps(),
    ...makeThemeProps()
  },
  emits: {
    reveal: (value: boolean) => true,
    focusin: (event: FocusEvent) => true
  },
  setup(props, { slots, emit, expose }) {
    const layout = useInjectLayout();
    const { themeClasses } = provideTheme(props);
    const { invertColorClasses } = useInvertColor(props, componentName);
    const { transparentClasses } = useTransparent(props, componentName);

    if (!Object.keys(layout).length) {
      warn('Footer', `CdsFooter component must be used inside of a CdsLayout component.`);
      return () => {};
    }

    const { size, revealed, windowHeight, revealOnFocus, classes, style, updateRevealed, updateLocal, onResize } =
      useLayout(props, componentName, 'F');
    const display = useDisplay();

    function onFocusin(event: FocusEvent) {
      if (unref(revealOnFocus)) {
        updateLocal(revealed, true);
      }

      emit('focusin', event);
    }

    watch(revealed, (value) => {
      layout.animate();
      emit('reveal', value);
    });

    watch([size, layout.scroll, layout.height], updateRevealed);

    watch(
      () => display.height,
      (value) => {
        !unref(layout.isContainer) && updateLocal(windowHeight, value);
      }
    );

    useRender(() => {
      const child = hMergeSlot(slots.default, [
        h(LayoutResizeObserver, {
          debounce: 0,
          onResize
        })
      ]);

      return h(
        'footer',
        {
          class: classNames(unref(classes), unref(invertColorClasses), unref(transparentClasses), unref(themeClasses)),
          style: unref(style),
          onFocusin
        },
        child
      );
    });

    expose({
      ...layout
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsFooter';
</style>
